// A self-contained QR code, rendered as inline SVG (React elements — no <img>,
// no data-URI, no network) so it is safe under the viewer's strict CSP
// (script-src 'self'; img-src 'self' data: blob:). The matrix is computed in
// the browser by the `qr` package (zero-dependency, pure JS).
//
// The modules are always drawn dark-on-white regardless of the app theme: an
// inverted (light-on-dark) code is unreliable for authenticator-app cameras, so
// the code keeps its own white quiet-zone background in both light and dark UI.

import { useMemo } from "react";
import encodeQR from "qr";

export function QrCode({
  value,
  ecc = "medium",
  className,
  title = "QR code",
}: {
  value: string;
  ecc?: "low" | "medium" | "quartile" | "high";
  className?: string;
  title?: string;
}) {
  // encodeQR("…", "raw", …) returns a boolean[][] module matrix (true = dark)
  // that already includes the quiet-zone border. Memoize so we only re-encode
  // when the payload changes. Encoding can throw (e.g. payload too large for a
  // QR symbol); surface null so callers still show the manual fallback.
  const matrix = useMemo<boolean[][] | null>(() => {
    try {
      return encodeQR(value, "raw", { ecc, border: 4 });
    } catch {
      return null;
    }
  }, [value, ecc]);

  if (!matrix || matrix.length === 0) return null;

  const size = matrix.length;
  // Build a single path of 1×1 squares for every dark module — one <path> keeps
  // the DOM light versus one <rect> per module.
  let d = "";
  for (let r = 0; r < size; r++) {
    const row = matrix[r] ?? [];
    for (let c = 0; c < row.length; c++) {
      if (row[c]) d += `M${c} ${r}h1v1h-1z`;
    }
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      role="img"
      aria-label={title}
      className={className}
    >
      <title>{title}</title>
      {/* White quiet-zone background so the code scans in light and dark themes. */}
      <rect x="0" y="0" width={size} height={size} fill="#ffffff" />
      <path d={d} fill="#000000" />
    </svg>
  );
}
